import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { me, handoff } from '../lib/api.js';
import { isPushSupported, isStandalone, subscribeToPush } from '../lib/push.js';
import { appIcon, appColor } from '../lib/appMeta.js';

export default function Apps() {
  const [apps, setApps] = useState(null);
  const [error, setError] = useState('');
  const [pushBanner, setPushBanner] = useState(false);
  const [handoffURL, setHandoffURL] = useState(null);

  useEffect(() => {
    me.apps().then(r => setApps(r.apps)).catch(e => setError(e.message));
    if (isPushSupported() && isStandalone() && Notification.permission === 'default') {
      setPushBanner(true);
    }
  }, []);

  // v160.4.96c: same stale-data fix as AppDetail. When iOS reactivates
  // the PWA from background or the SW notifies us about a notification
  // tap, refetch the apps list so any new apps a tester was added to
  // appear without manual refresh.
  useEffect(() => {
    let cancelled = false;
    function silentRefresh(reason) {
      console.info('[Apps] silent refresh', { reason });
      me.apps()
        .then(r => { if (!cancelled) setApps(r.apps); })
        .catch(err => console.warn('[Apps] silent refresh failed', err.message));
    }
    function onMessage(event) {
      const msg = event?.data;
      if (msg?.type === 'velkra-dist-refresh') {
        silentRefresh(`sw:${msg.reason || 'unknown'}`);
      }
    }
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') silentRefresh('visibilitychange');
    }
    function onFocus() { silentRefresh('window-focus'); }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onMessage);
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onMessage);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  async function enablePush() {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return setPushBanner(false);
      await subscribeToPush();
      setPushBanner(false);
    } catch (e) { setError(e.message); }
  }

  async function generateHandoff(appSlug = 'all') {
    try {
      const r = await handoff.start(appSlug);
      setHandoffURL({ ...r, appSlug });
    } catch (e) { alert(e.message); }
  }

  return (
    <div>
      {error && <div className="error-banner">{error}</div>}
      {pushBanner && (
        <div className="card" style={{ background: 'rgba(108,92,231,0.15)', borderLeft: '3px solid var(--accent)' }}>
          <strong>Get notified about new builds</strong>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '4px 0 12px' }}>
            Push notifications work on iPhone when this is added to your Home Screen.
          </p>
          <button className="btn" onClick={enablePush}>Enable notifications</button>
        </div>
      )}

      <div className="card">
        <h2>Your apps</h2>
        {apps === null ? <div className="spinner" /> : null}
        {apps && apps.length === 0 && (
          <div className="empty">
            No apps yet — upload your first build via StellarFixerWeb's Build &amp; Distribute panel and it'll appear here.
          </div>
        )}
        {apps && apps.length > 0 && (
          <div className="list">
            {apps.map(a => (
              <Link to={`/apps/${a.app_slug}`} key={a.app_slug} className="list-row">
                <div className="icon" style={{ background: appColor(a.app_slug) + '22', color: appColor(a.app_slug), fontSize: 24 }}>{appIcon(a.app_slug)}</div>
                <div className="body">
                  <div className="title">{a.app_name || a.app_slug}</div>
                  <div className="sub">
                    {a.latest_version
                      ? `v${a.latest_version} · ${timeAgo(a.latest_uploaded_at)}`
                      : 'No builds yet'}
                  </div>
                </div>
                <div className="arrow">›</div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h2>📲 Connect iOS apps</h2>
        <p style={{fontSize:14,color:'var(--text-muted)',margin:'4px 0 12px'}}>
          Sign in once per app on the device that has the app installed. The link below opens the app
          directly and seeds a tester JWT so feedback (shake, long-press, element picker) submits to
          this dist site. Open this page in Mobile Safari on your iPhone.
        </p>
        {/*
          v160.4.99: Per-app sign-in links. The previous "Generate sign-in link"
          button always called handoff.start('all'), which made the backend
          return a velkra-dist://auth?... URL — BUT no app declares the
          velkra-dist:// scheme in Info.plist (verified across all 50 apps:
          they each declare only their own scheme like aquatrack://). So the
          one-and-only sign-in link couldn't open ANY installed app, and every
          tester ended up with a nil tokenProvider() result. Per-app handoff
          uses each app's own scheme (aquatrack://velkra-dist-auth?t=...),
          which the apps already handle via VDFDeepLink + .onOpenURL.
        */}
        {apps && apps.length > 0 && (
          <div className="list">
            {apps.map(a => {
              const isActive = handoffURL && handoffURL.appSlug === a.app_slug;
              return (
                <div key={a.app_slug} className="list-row" style={{flexWrap:'wrap'}}>
                  <div className="icon" style={{ background: appColor(a.app_slug) + '22', color: appColor(a.app_slug), fontSize: 20 }}>{appIcon(a.app_slug)}</div>
                  <div className="body">
                    <div className="title">{a.app_name || a.app_slug}</div>
                    {!isActive && (
                      <div className="sub" style={{fontSize:12}}>
                        Tap to generate a one-time sign-in link.
                      </div>
                    )}
                    {isActive && (
                      <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>
                        Link expires in {Math.floor(handoffURL.expiresInSeconds / 60)} min · one-time use.
                      </div>
                    )}
                  </div>
                  {!isActive && (
                    <button className="btn" style={{padding:'6px 12px',fontSize:13}}
                            onClick={() => generateHandoff(a.app_slug)}>
                      Sign in
                    </button>
                  )}
                  {isActive && (
                    <a className="install-cta" style={{padding:'6px 12px',fontSize:13}}
                       href={handoffURL.handoffURL}>
                      Open {a.app_name || a.app_slug}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {(!apps || apps.length === 0) && (
          <p style={{fontSize:13,color:'var(--text-muted)'}}>
            Once builds are uploaded, each app will appear here with its own sign-in link.
          </p>
        )}
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
